import { useRef, useEffect, useCallback } from 'react';

interface SpriteAnimatorProps {
  src: string;
  frameWidth: number;
  frameHeight: number;
  columns: number;
  totalFrames: number;
  fps?: number;
  /** Bump this value to trigger one play-through. */
  trigger?: number;
  className?: string;
  style?: React.CSSProperties;
  /** Called when one play-through finishes. */
  onComplete?: () => void;
}

/**
 * Sprite-sheet animator that stays on frame 0 until triggered.
 * Each change to `trigger` plays the animation once, then returns to frame 0.
 */
export function SpriteAnimator({
  src,
  frameWidth,
  frameHeight,
  columns,
  totalFrames,
  fps = 12,
  trigger = 0,
  className,
  style,
  onComplete,
}: SpriteAnimatorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const frameRef = useRef(0);
  const lastTimeRef = useRef(0);
  const playingRef = useRef(false);
  const rafIdRef = useRef(0);
  const drawnOnceRef = useRef(false);

  const drawFrame = useCallback((frame: number) => {
    const img = imgRef.current;
    const ctx = canvasRef.current?.getContext('2d');
    if (!img?.complete || !ctx) return;

    const col = frame % columns;
    const row = Math.floor(frame / columns);
    ctx.clearRect(0, 0, frameWidth, frameHeight);
    ctx.drawImage(
      img,
      col * frameWidth, row * frameHeight, frameWidth, frameHeight,
      0, 0, frameWidth, frameHeight,
    );
  }, [columns, frameWidth, frameHeight]);

  // Load image and draw frame 0
  useEffect(() => {
    const img = new Image();
    img.src = src;
    imgRef.current = img;
    img.onload = () => {
      drawFrame(0);
      drawnOnceRef.current = true;
    };
  }, [src, drawFrame]);

  // Play once when trigger changes (skip initial mount)
  useEffect(() => {
    if (trigger === 0) return;

    playingRef.current = true;
    frameRef.current = 0;
    lastTimeRef.current = 0;

    const interval = 1000 / fps;

    const tick = (time: number) => {
      if (!playingRef.current) return;

      if (lastTimeRef.current === 0) lastTimeRef.current = time;

      const delta = time - lastTimeRef.current;
      if (delta >= interval) {
        lastTimeRef.current = time - (delta % interval);
        drawFrame(frameRef.current);
        frameRef.current++;

        if (frameRef.current >= totalFrames) {
          playingRef.current = false;
          drawFrame(0);
          onComplete?.();
          return;
        }
      }

      rafIdRef.current = requestAnimationFrame(tick);
    };

    rafIdRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafIdRef.current);
  }, [trigger, fps, totalFrames, drawFrame, onComplete]);

  return (
    <canvas
      ref={canvasRef}
      width={frameWidth}
      height={frameHeight}
      className={className}
      style={style}
    />
  );
}
